import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { getTeamRoles, createTeamRole, updateTeamRole, deleteTeamRole } from '../../api/client'

const DISCIPLINES = ['mec', 'elect', 'software', 'factory', 'otros']

// The Cargo dropdown itself, managed here rather than hardcoded -- adding a
// role a PM actually needs (instead of them typing something close enough)
// is the whole point, so this has to be reachable without a code change.
// `inline`: the panel as a page section (Administración > Cargos) instead of a modal.
export default function RolesPanel({ onClose, onChanged, inline = false }) {
  const { t } = useTranslation('engineering')
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [newName, setNewName] = useState('')
  const [newDiscipline, setNewDiscipline] = useState('otros')
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    getTeamRoles().then(data => { setRoles(data); setLoading(false) })
  }
  useEffect(() => { load() }, [])

  const notifyChanged = () => { load(); onChanged?.() }

  const handleAdd = async () => {
    if (!newName.trim()) { setError(t('team.roleNameRequired')); return }
    setSaving(true); setError('')
    try {
      await createTeamRole({ name: newName.trim(), discipline: newDiscipline })
      setNewName(''); setNewDiscipline('otros')
      notifyChanged()
    } catch (e) { setError(e.response?.data?.detail || 'Save failed') }
    setSaving(false)
  }

  const handleRename = async (role, name) => {
    if (name === role.name) return
    if (!name.trim()) return
    try { await updateTeamRole(role.id, { name: name.trim() }); notifyChanged() }
    catch (e) { setError(e.response?.data?.detail || 'Save failed') }
  }

  const handleDiscipline = async (role, discipline) => {
    try { await updateTeamRole(role.id, { discipline }); notifyChanged() }
    catch (e) { setError(e.response?.data?.detail || 'Save failed') }
  }

  const handleDelete = async (role) => {
    if (!confirm(t('team.roleDeleteConfirm', { name: role.name }))) return
    try { await deleteTeamRole(role.id); notifyChanged() }
    catch (e) { setError(e.response?.data?.detail || 'Delete failed') }
  }

  // A plain div or the Modal -- element types that stay the same between
  // renders, so the inputs keep their focus while typing.
  const envolver = contenido => inline
    ? <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl">{contenido}</div>
    : <Modal title={t('team.rolesPanelTitle')} onClose={onClose} wide>{contenido}</Modal>

  return envolver(
    <>
      <p className="text-xs text-gray-400 mb-4">{t('team.rolesPanelHint')}</p>
      {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg mb-3">{error}</p>}

      {loading ? (
        <div className="text-gray-400 text-sm">{t('team.loading')}</div>
      ) : (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-2 font-semibold text-gray-600">{t('team.roleHeader')}</th>
              <th className="text-left py-2 font-semibold text-gray-600">{t('team.disciplineHeader')}</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {roles.map(role => (
              <tr key={role.id} className="border-b border-gray-50">
                <td className="py-2 pr-3">
                  <input
                    defaultValue={role.name}
                    onBlur={e => handleRename(role, e.target.value)}
                    className="w-full border border-transparent hover:border-gray-200 focus:border-gray-300 rounded px-2 py-1 text-sm focus:outline-none"
                  />
                </td>
                <td className="py-2 pr-3">
                  <select
                    value={role.discipline}
                    onChange={e => handleDiscipline(role, e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  >
                    {DISCIPLINES.map(d => (
                      <option key={d} value={d}>{t(`team.disciplines.${d}`)}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 text-right">
                  <button onClick={() => handleDelete(role)}
                          className="text-gray-400 hover:text-red-500 transition-colors text-lg leading-none px-1">
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex gap-2 items-center pt-2 border-t border-gray-100">
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd() }}
          placeholder={t('team.roleNamePlaceholder')}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
        />
        <select
          value={newDiscipline}
          onChange={e => setNewDiscipline(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
        >
          {DISCIPLINES.map(d => (
            <option key={d} value={d}>{t(`team.disciplines.${d}`)}</option>
          ))}
        </select>
        <button onClick={handleAdd} disabled={saving}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors whitespace-nowrap">
          {t('team.addRole')}
        </button>
      </div>
    </>
  )
}
